/**
 * PSD `LayerTextData` → editor text-layer descriptor.
 *
 * Tier 2 Group F PSD natives follow-up (item 2a-iv) — closes the
 * deferred "PSD text layer native mapping" from the PSD round-trip
 * plan. Imports ag-psd's text records into the editor's text
 * LayerNode + TextSettings so Photoshop text layers appear as
 * editable IText objects instead of rasterizing.
 *
 * ## Scope (v1)
 *
 * - **Single-style text**: the first `styleRun` provides the
 *   layer-wide settings (font, size, weight, italic, fill, etc.).
 *   Per-run style variance (different fonts or colors within one
 *   text layer) requires editor-model expansion that's out of v1
 *   scope; layers carrying multiple distinct styleRuns are
 *   accepted but use only the first run's style — the rest of the
 *   text inherits.
 * - **Multi-line via PSD's `\r` paragraph break**: PSD stores
 *   newlines as carriage returns; we normalize to `\n` for the
 *   editor's IText, which handles `\n` natively.
 * - **Paragraph alignment** from the first `paragraphStyleRun`'s
 *   `justification` → editor `textAlign`.
 * - **Position**: derived from `LayerTextData.transform` (the 6-
 *   element affine [a, b, c, d, e, f]). Translate is `(e, f)`;
 *   we treat the y as a baseline and apply the same 0.8 baseline-
 *   to-top adjustment the SVG path uses for consistency.
 *
 * ## Rejected (raster fallback via the existing path)
 *
 * - `warp` set (text warped along an arc / flag / etc.)
 * - `textPath` set (text-on-path is read-only in ag-psd; can't
 *   round-trip on export)
 * - `gridInfo` enabled (Asian-style grid layout)
 * - `transform` carries rotation or skew components (editor text-
 *   layer model has no per-layer rotation)
 *
 * Pure module — no DOM dependencies. Tests inject synthetic
 * ag-psd `Layer` shapes.
 */

import type { Layer as PsdLayer, TextStyle, ParagraphStyle } from 'ag-psd';
import type { TextSettings } from '../types';
import type { ParsedSvgTextLayer } from './svg-text-import';
import { BASELINE_TO_TOP_RATIO } from './text-baseline-constants';
import { psdColorToHex } from './psd-color-utils';

/** Reasons a PSD text layer can't be expressed natively. Caller
 *  routes the failure to the existing raster fallback. */
export type PsdTextRejectReason =
  | 'no-text-data'
  | 'has-warp'
  | 'has-text-path'
  | 'has-text-grid'
  | 'transform-non-trivial'
  | 'empty-text';

export type PsdTextImportResult =
  | { ok: true; value: ParsedSvgTextLayer }
  | { ok: false; reason: PsdTextRejectReason };

/** Default font size — ag-psd's `styleRun[0].style.fontSize` is
 *  usually present, but a stripped text record can omit it.
 *  16px matches the editor's default for new text layers. */
const DEFAULT_FONT_SIZE = 16;

/** Default font family used when the styleRun's font.name is
 *  missing. Sans-serif chosen because Photoshop's default new-
 *  text font is Myriad Pro (sans-serif); when import sees no
 *  font, falling back to a generic sans keeps the layer visually
 *  closer than 'serif'. */
const DEFAULT_FONT_FAMILY = 'sans-serif';

// BASELINE_TO_TOP_RATIO imported from text-baseline-constants.ts —
// shared with all four SVG/PSD text modules.

/** Parse the PSD layer's text record into a native text
 *  descriptor. The caller's walker emits a text-type LayerNode
 *  + the textDescriptors entry; EditorPage's loadPsdFile
 *  materializes the IText via `canvas.addTextLayer`. */
export function parsePsdText(psdLayer: PsdLayer): PsdTextImportResult {
  if (!psdLayer.text) return { ok: false, reason: 'no-text-data' };
  const td = psdLayer.text;

  // Reject features the editor's text model can't carry.
  if (td.warp && td.warp.style && td.warp.style !== 'none') {
    return { ok: false, reason: 'has-warp' };
  }
  if (td.textPath) return { ok: false, reason: 'has-text-path' };
  if (td.gridInfo?.isOn) return { ok: false, reason: 'has-text-grid' };

  // Normalize PSD's `\r` paragraph break to `\n` (Fabric IText's
  // convention). Photoshop emits `\r` as the line terminator
  // historically; modern PSDs may use `\r\n` or just `\n`.
  // Collapse all variants to `\n`.
  const normalizedText = td.text.replace(/\r\n|\r/g, '\n');
  if (!normalizedText.trim()) return { ok: false, reason: 'empty-text' };

  // Transform: [a, b, c, d, e, f]. We accept identity + translate
  // + uniform scale; reject anything with a rotation/skew/non-
  // uniform component. Rotated text would need editor-model
  // angle support that's a Tier 3 follow-up.
  const transform = td.transform;
  let translateX = 0;
  let translateY = 0;
  let scale = 1;
  if (transform && transform.length >= 6) {
    const [a, b, c, d, e, f] = transform;
    const EPS = 1e-4;
    // Pure scale + translate: b=c=0, a≈d. Anything else rejects.
    if (Math.abs(b) > EPS || Math.abs(c) > EPS) {
      return { ok: false, reason: 'transform-non-trivial' };
    }
    if (Math.abs(a - d) > EPS) {
      // Non-uniform scale — text would need different x/y scales.
      // Editor's text model uses a single fontSize; non-uniform
      // would require glyph-width manipulation outside our scope.
      return { ok: false, reason: 'transform-non-trivial' };
    }
    // Negative scale = mirror flip. Photoshop allows mirrored
    // text layers but the editor's TextSettings.fontSize is a
    // positive scalar with no mirror flag, so a negative `a` /
    // `d` would cascade into a negative fontSize. Reject here —
    // the stash preserves the original transform for export-
    // side fidelity. (Pre-PR review R1 Critical.)
    if (a < 0 || d < 0) {
      return { ok: false, reason: 'transform-non-trivial' };
    }
    translateX = e;
    translateY = f;
    scale = a;
  }

  // Style: take the first styleRun's style as the layer-wide
  // settings. Subsequent runs would imply per-character styling
  // that the editor's TextSettings doesn't carry; their styles
  // are silently dropped (the raster fallback would have lost
  // them too).
  const primaryStyle: TextStyle = td.styleRuns?.[0]?.style ?? td.style ?? {};
  const primaryPara: ParagraphStyle = td.paragraphStyleRuns?.[0]?.style ?? td.paragraphStyle ?? {};

  const fontFamily = primaryStyle.font?.name ?? DEFAULT_FONT_FAMILY;
  const fontSizeRaw = primaryStyle.fontSize ?? DEFAULT_FONT_SIZE;
  // ag-psd stores fontSize in points (or document units depending
  // on shapeType); we approximate to pixels by scaling through
  // the transform's scale factor.
  const fontSize = Math.max(1, fontSizeRaw * scale);

  const fontWeight: TextSettings['fontWeight'] = primaryStyle.fauxBold ? 'bold' : 'normal';
  const fontStyle: TextSettings['fontStyle'] = primaryStyle.fauxItalic ? 'italic' : 'normal';
  const underline = primaryStyle.underline === true;
  const textAlign: TextSettings['textAlign'] = mapJustification(primaryPara.justification);
  const fill =
    primaryStyle.fillFlag === false ? 'transparent' : psdColorToHex(primaryStyle.fillColor);

  // Letter spacing: ag-psd's `tracking` is in 1/1000 em — same
  // unit the editor uses internally. Pass through directly.
  const letterSpacing = primaryStyle.tracking ?? 0;

  // Line height: PSD's `leading` is the line-height in points
  // (or PSD-document units). When `autoLeading` is true, PSD
  // computes leading as 120% of font size (per Adobe convention).
  // The editor's lineHeight is a unitless multiplier.
  let lineHeight: number;
  if (primaryStyle.autoLeading !== false) {
    lineHeight = 1.2; // Adobe default
  } else if (primaryStyle.leading && fontSize > 0) {
    lineHeight = primaryStyle.leading / fontSize;
  } else {
    lineHeight = 1.16; // Fabric IText default
  }

  // Stroke: PSD's outlineWidth is the stroke width in document
  // units; strokeColor is the stroke color. Treat strokeFlag = false
  // as no stroke.
  const strokeWidth = primaryStyle.outlineWidth ?? 0;
  const stroke =
    primaryStyle.strokeFlag !== false && strokeWidth > 0
      ? {
          color: psdColorToHex(primaryStyle.strokeColor),
          width: strokeWidth * scale,
          position: 'center' as const,
        }
      : undefined;

  // Position: PSD's text bounds give the baseline anchor for
  // point text. We use the transform's (e, f) translation as the
  // baseline x/y. Editor's text layer model uses top-left bbox
  // origin → subtract fontSize * 0.8 from y (matches SVG).
  const baselineX = translateX;
  const baselineY = translateY;
  const topY = baselineY - fontSize * BASELINE_TO_TOP_RATIO;

  const settings: TextSettings = {
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    underline,
    textAlign,
    fill,
    arch: 0,
    letterSpacing,
    lineHeight,
    ...(stroke ? { stroke } : {}),
  };

  return {
    ok: true,
    value: {
      text: normalizedText,
      x: baselineX,
      y: topY,
      settings,
    },
  };
}

/** Map PSD `ParagraphStyle.justification` → editor `textAlign`.
 *  PSD's `justify-*` variants all collapse to the closest non-
 *  justified alignment (editor doesn't model justified text). */
function mapJustification(
  j: ParagraphStyle['justification'] | undefined,
): TextSettings['textAlign'] {
  switch (j) {
    case 'right':
    case 'justify-right':
      return 'right';
    case 'center':
    case 'justify-center':
    case 'justify-all':
      return 'center';
    case 'left':
    case 'justify-left':
    default:
      return 'left';
  }
}

// psdColorToHex imported from psd-color-utils.ts — shared with
// psd-vector-import.ts so a future RGB-edge-case fix lands once.
